import React from 'react'

import {
  ItemSelector,
  type ItemSelectorProps,
} from '~/components/elements/ItemSelector'
import { type SurveyGroupAnswerOption as SurveyGroupAnswerOptionBase } from '~/gql/graphql'
import {
  SurveyGroupAnswerOptionOp,
  type Unassigned,
  unassignedSymbol,
} from '~/utils'

type SurveyGroupAnswerOption = App.PickGraphQL<
  SurveyGroupAnswerOptionBase,
  'option' | 'text'
>

export type SurveyGroupAnswerOptionSelectorProps<
  TSurveyGroupAnswerOption extends SurveyGroupAnswerOption,
> = Omit<
  ItemSelectorProps,
  'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
> & {
  answerOptions: TSurveyGroupAnswerOption[]
  onChange?: (
    answerOption: TSurveyGroupAnswerOption['option'] | Unassigned,
  ) => void
  value: TSurveyGroupAnswerOption['option'] | Unassigned
}

export const SurveyGroupAnswerOptionSelector = React.forwardRef(
  <TSurveyGroupAnswerOption extends SurveyGroupAnswerOption>(
    {
      answerOptions,
      onChange,
      value: valueProp,
      ...rest
    }: SurveyGroupAnswerOptionSelectorProps<TSurveyGroupAnswerOption>,
    ref: React.ForwardedRef<HTMLSpanElement>,
  ) => {
    const data = React.useMemo(
      () =>
        answerOptions.map((option) => ({
          ...option,
          textField: SurveyGroupAnswerOptionOp.toText(option),
        })),
      [answerOptions],
    )
    const value = React.useMemo(
      () =>
        data.find(({ option }) => option === valueProp) ?? {
          option: unassignedSymbol,
        },
      [data, valueProp],
    )

    const handleChange = React.useCallback<EH<ItemSelectorProps, 'onChange'>>(
      (answerOption: TSurveyGroupAnswerOption) => {
        onChange?.(answerOption.option)
      },
      [onChange],
    )

    return (
      <ItemSelector
        ref={ref}
        data={data}
        dataItemKey="option"
        textField="textField"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
